import React, { useEffect, useState } from "react";
import { Lock, ShieldCheck, CheckCircle2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

/** Tracks + submits an unlock request for a given property/report pair. */
function useUnlockRequest(propertyId, report, user, promptLogin) {
  const [existing, setExisting] = useState(null); // {id, status} once requested
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!user) return;
    setChecking(true);
    api.get("/my/unlock-requests")
      .then((r) => {
        const match = (r.data.items || []).find(
          (x) => x.property_id === propertyId && x.report === report
        );
        setExisting(match || null);
      })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, [user, propertyId, report]);

  const requestUnlock = async () => {
    if (!user) { promptLogin(); return; }
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/unlock-requests", { property_id: propertyId, report });
      setExisting({ id: data.id, status: data.status });
      if (data.already_requested) {
        toast.info("You've already requested this report. Tharlo will get back to you shortly.");
      } else {
        toast.success("Request received. A Tharlo advisor will contact you with the report details.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send request. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return { isRequested: !!existing, busy, checking, requestUnlock };
}

function UnlockCta({ report, user, isRequested, busy, checking, onUnlock, onLogin }) {
  if (!user) {
    return (
      <button
        onClick={onLogin}
        data-testid={`unlock-signup-${report}`}
        className="btn-gold w-full sm:w-auto !py-3 !px-6"
      >
        Sign up to Unlock
      </button>
    );
  }
  if (isRequested) {
    return (
      <div data-testid={`unlock-requested-${report}`} className="inline-flex items-center gap-2 text-gold text-sm font-semibold">
        <CheckCircle2 className="w-4 h-4" />
        <span>Request received — Tharlo will follow up</span>
      </div>
    );
  }
  return (
    <button
      onClick={onUnlock}
      disabled={busy || checking}
      data-testid={`unlock-request-${report}`}
      className="btn-gold w-full sm:w-auto !py-3 !px-6"
    >
      {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : "Request Unlock"}
    </button>
  );
}

function BlurredFields({ fields, dividerColor, subtleText, dark }) {
  return (
    <div className={`relative border-t ${dividerColor} pt-5 mt-5`}>
      <div className="space-y-3 select-none pointer-events-none" aria-hidden="true">
        {fields.map((f) => (
          <div key={f.label} className={`flex items-baseline justify-between gap-4 pb-2 border-b ${dividerColor}`}>
            <span className={`text-xs uppercase tracking-widest ${subtleText}`}>{f.label}</span>
            <span className="text-sm font-semibold blur-sm select-none tracking-wider" style={{ letterSpacing: "0.1em" }}>
              {f.blurValue || "•••••••"}
            </span>
          </div>
        ))}
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <Lock className={`w-6 h-6 ${dark ? "text-white/40" : "text-muted-foreground"}`} />
      </div>
    </div>
  );
}

/**
 * A blurred paid-report card. Renders on Property Detail pages.
 *
 * Props:
 *  - propertyId: string
 *  - report: "trust_location" | "trust_legal" | "fair_price"
 *  - title: heading (e.g. "Location & Info Verification")
 *  - subtitle: one-line description of what's inside
 *  - fields: [{ label, blurValue }] — decorative rows shown blurred
 *  - variant: "dark" | "light" — Trust Score uses dark, Fair Price uses light
 */
export default function PaidReportBlock({
  propertyId,
  report,
  title,
  subtitle,
  fields = [],
  variant = "dark",
}) {
  const { user, promptLogin } = useAuth();
  const { isRequested, busy, checking, requestUnlock } = useUnlockRequest(propertyId, report, user, promptLogin);

  const dark = variant === "dark";
  const outer = dark ? "bg-ink text-white" : "bg-white text-ink border border-border";
  const dividerColor = dark ? "border-white/10" : "border-border";
  const subtleText = dark ? "text-white/60" : "text-muted-foreground";

  return (
    <div className={`${outer} p-6 lg:p-8`} data-testid={`paid-report-${report}`}>
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-10 h-10 flex items-center justify-center ${dark ? "bg-gold/10 text-gold" : "bg-ink text-gold"} shrink-0`}>
          <ShieldCheck className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <div className="overline text-gold">Tharlo Verified · Paid</div>
          <h3 className={`font-display text-xl lg:text-2xl mt-0.5 ${dark ? "" : "text-ink"}`}>{title}</h3>
          <p className={`text-xs mt-1 ${subtleText}`}>{subtitle}</p>
        </div>
      </div>

      <BlurredFields fields={fields} dividerColor={dividerColor} subtleText={subtleText} dark={dark} />

      {/* Unlock CTA */}
      <div className={`mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-5 border-t ${dividerColor}`}>
        <div className="text-xs">
          <div className={`${dark ? "text-white/80" : "text-ink"} font-semibold`}>Pricing to be announced</div>
          <div className={subtleText}>Tharlo is currently in testing. Request unlock and our advisor will share the report scope + pricing with you.</div>
        </div>
        <UnlockCta
          report={report}
          user={user}
          isRequested={isRequested}
          busy={busy}
          checking={checking}
          onUnlock={requestUnlock}
          onLogin={promptLogin}
        />
      </div>
    </div>
  );
}
